import Select from "react-select";
import { IDropdownOption } from "../../../utils/customisation";
import { useEffect, useState } from "react";
import apiAgent from "../../../api/apiAgent";
import _, { orderBy } from "lodash";
import { mergeClass } from "../../../utils";
import { useAtom } from "jotai";
import {
  isCardViewAtom,
  orderAtom,
  placeFilterAtom,
  sortByAtom,
  topicFilterAtom,
} from "../../../atoms";

const selectStyles = {
  control: (baseStyles: any, state: any) => ({
    ...baseStyles,
    borderRadius: 0,
    boxShadow: 0,
    borderColor: state.isFocused ? "#000000" : "#B3B3B3",
    "&:hover": {
      borderColor: state.isFocused ? "#000000" : "#000000",
    },
  }),
};

export const defaultFilterOption: IDropdownOption = {
  label: "Show all",
  value: "none",
};

export const momentSortOptions: IDropdownOption[] = [
  {
    label: "Last modified",
    value: "last modified",
  },
  {
    label: "Alphabetical",
    value: "alphabetical",
  },
  {
    label: "Date",
    value: "date",
  },
];

export const momentOrderOptions: IDropdownOption[] = [
  {
    label: "Ascending",
    value: "asc",
  },
  {
    label: "Descending",
    value: "desc",
  },
];

const SortDropdownContainer = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-1">
    <div>{title}</div>
    <div>{children}</div>
  </div>
);

const ListViewButton = ({
  className,
  children,
  highlight,
  ...props
}: React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
> & { highlight: boolean }) => (
  <button
    className={mergeClass(
      "grid content-center transition border-3 border-primaryColor",
      {
        "opacity-100": highlight,
        "opacity-60 hover:opacity-80": !highlight,
      },
      className
    )}
    disabled={highlight}
    {...props}
  >
    {children}
  </button>
);

const SortMomentsMenu = () => {
  const [sortBy, setSortBy] = useAtom(sortByAtom);
  const [order, setOrder] = useAtom(orderAtom);
  const [topic, setTopic] = useAtom(topicFilterAtom);
  const [place, setPlace] = useAtom(placeFilterAtom);

  const [topicOptions, setTopicOptions] = useState<IDropdownOption[]>([]);
  const [placeOptions, setPlaceOptions] = useState<IDropdownOption[]>([]);
  const [isCardView, setIsCardView] = useAtom(isCardViewAtom);

  useEffect(() => {
    if (topicOptions.length < 1) fetchOptions("topics", setTopicOptions);
    if (placeOptions.length < 1) fetchOptions("place", setPlaceOptions);
  }, []);

  const fetchOptions = async (
    siName: string,
    setOptions: (options: IDropdownOption[]) => void
  ) => {
    try {
      const response = await apiAgent.SingleItem.get(siName);
      if (response.status === 200) {
        const data = JSON.parse(response.data.data);
        const parseOptions = data.values.map((item: string) => {
          return {
            label: item,
            value: item,
          };
        });
        setOptions([defaultFilterOption, ..._.sortBy(parseOptions, "value")]);
      }
    } catch (err) {
      console.log(err);
    } finally {
    }
  };

  return (
    <div className="flex gap-6 text-xs text-grayText font-bold mb-5">
      <div className="w-full grid grid-cols-4 gap-4">
        <SortDropdownContainer title="Sort by">
          <Select
            styles={selectStyles}
            options={momentSortOptions}
            isSearchable={false}
            value={sortBy}
            onChange={(e) => setSortBy(e || momentSortOptions[0])}
          />
        </SortDropdownContainer>
        <SortDropdownContainer title="Order">
          <Select
            styles={selectStyles}
            options={momentOrderOptions}
            isSearchable={false}
            value={order}
            onChange={(e) => setOrder(e || momentOrderOptions[0])}
          />
        </SortDropdownContainer>
        <SortDropdownContainer title="Filter by topic">
          <Select
            styles={selectStyles}
            options={topicOptions}
            value={topic}
            onChange={(e) => setTopic(e || defaultFilterOption)}
          />
        </SortDropdownContainer>
        <SortDropdownContainer title="Filter by place">
          <Select
            styles={selectStyles}
            options={placeOptions}
            value={place}
            onChange={(e) => setPlace(e || defaultFilterOption)}
          />
        </SortDropdownContainer>
      </div>
      <div>
        <SortDropdownContainer title="View">
          <div className="h-[38px] flex text-4xl gap-1">
            <ListViewButton
              highlight={!isCardView}
              onClick={() => setIsCardView(false)}
            >
              <i className="icon-list-view_highlighted" />
            </ListViewButton>
            <ListViewButton
              highlight={isCardView}
              onClick={() => setIsCardView(true)}
            >
              <i className="icon-grid-view_highlighted" />
            </ListViewButton>
          </div>
        </SortDropdownContainer>
      </div>
    </div>
  );
};

export { SortMomentsMenu };
